import { Tabs, Typography, Tag, Collapse, Alert, Empty } from "antd";
import { useTranslation } from "react-i18next";
import { FileTextOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { TextoClinico } from "../../../../../components/uiComponents/TextoClinico";
import "./SessionVrInfoPanel.scss";

const { Title, Text } = Typography;

// Descripcion, guion y guia: texto de un profesional, con sus parrafos. Se
// pinta con TextoClinico (HTML saneado o texto plano con saltos y negritas);
// antes el texto plano iba a un <p> y salia todo seguido.
function HtmlOrText({ value, emptyText = "—" }) {
  return (
    <TextoClinico
      valor={value}
      className="html-content"
      vacio={<Text type="secondary">{emptyText}</Text>}
    />
  );
}

function normalizeObjectives(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .filter(Boolean)
      .map((x) => String(x).trim())
      .filter(Boolean);
  }

  // string tipo:
  // "1. Conseguir movilidad\n2- Divertir al paciente"
  if (typeof raw === "string") {
    return raw
      .split(/\r?\n/)
      .map((line) => line.replace(/^\s*\d+\s*[.-]\s*/g, "").trim())
      .filter(Boolean);
  }

  return [];
}

export function SessionVrInfoPanel({ therapy, virtualScene }) {
  // Solo se traducen los ROTULOS. El titulo de la terapia, sus objetivos, su
  // guion y la descripcion clinica de la sala son contenido que escribio el
  // terapeuta: eso no se toca.
  const { t } = useTranslation(["therapy", "common"]);

  // --- Terapia ---
  const therapyTitle = therapy?.title || t("sesion.contexto.terapiaSinTitulo");

  const therapyDescription =
    therapy?.description ||
    therapy?.generalDescription ||
    therapy?.descripcion ||
    "";

  const therapyObjectives = normalizeObjectives(
    therapy?.objectives ||
      therapy?.therapeuticObjectives ||
      therapy?.goals ||
      therapy?.objetivos ||
      therapy?.objetivosTerapeuticos,
  );

  const therapyScript =
    therapy?.script || therapy?.guion || therapy?.therapyScript || "";

  // --- Sala ---
  const sceneTitle =
    virtualScene?.module_01?.ModOneTitle || t("sesion.contexto.salaSinTitulo");

  const sceneClinicalDescription =
    virtualScene?.module_07?.ModSevenClinicalDescription ||
    virtualScene?.module_07?.clinicalDescription ||
    "";

  const sceneFocus =
    virtualScene?.module_07?.ModSevenClinicalFocus ||
    virtualScene?.module_07?.clinicalFocus ||
    virtualScene?.module_07?.focus ||
    [];

  const sceneInterventionGuide =
    virtualScene?.module_07?.ModSevenTherapyScript ||
    virtualScene?.module_07?.ModSevenInterventionGuide ||
    virtualScene?.module_07?.interventionGuide ||
    "";

  const sceneContraindications =
    virtualScene?.module_07?.ModSevenContraindications ||
    virtualScene?.module_07?.contraindications ||
    "";

  const sceneDifficulty =
    virtualScene?.module_07?.ModSevenDifficultyLevel ||
    virtualScene?.module_07?.difficultyLevel ||
    null;

  if (!therapy && !virtualScene) {
    return <Empty description={t("sesion.contexto.sinInformacion")} />;
  }

  const items = [
    {
      key: "therapy",
      label: (
        <span className="revi-session-context__tab">
          <FileTextOutlined /> {t("sesion.contexto.tabTerapia")}
        </span>
      ),
      children: (
        <div className="revi-session-context__content">
          <div className="revi-session-context__header">
            <Title level={5} style={{ margin: 0 }}>
              {therapyTitle}
            </Title>
          </div>

          {therapyDescription ? (
            <div className="revi-session-context__block">
              <Text type="secondary">
                {t("sesion.contexto.descripcionGeneral")}
              </Text>
              <HtmlOrText value={therapyDescription} />
            </div>
          ) : null}

          <div className="revi-session-context__block">
            <Text type="secondary">{t("sesion.contexto.objetivos")}</Text>
            {therapyObjectives.length ? (
              <ul className="revi-session-context__list">
                {therapyObjectives.map((o, idx) => (
                  <li key={`${idx}-${String(o).slice(0, 12)}`}>{o}</li>
                ))}
              </ul>
            ) : (
              <Text type="secondary">—</Text>
            )}
          </div>

          <div className="revi-session-context__block">
            <Text type="secondary">{t("sesion.contexto.guion")}</Text>
            {therapyScript ? (
              <HtmlOrText
                value={therapyScript}
                emptyText={t("sesion.contexto.sinGuion")}
              />
            ) : (
              <Text type="secondary">{t("sesion.contexto.sinGuion")}</Text>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "scene",
      label: (
        <span className="revi-session-context__tab">
          <EnvironmentOutlined /> {t("sesion.contexto.tabSala")}
        </span>
      ),
      children: (
        <div className="revi-session-context__content">
          <div className="revi-session-context__header">
            <Title level={5} style={{ margin: 0 }}>
              {sceneTitle}
            </Title>
            {sceneDifficulty ? <Tag>{sceneDifficulty}</Tag> : null}
          </div>

          <Collapse
            className="revi-session-context__collapse"
            defaultActiveKey={["clinical", "focus", "guide", "warnings"]}
            items={[
              {
                key: "clinical",
                label: t("sesion.contexto.descripcionClinica"),
                children: <HtmlOrText value={sceneClinicalDescription} />,
              },
              {
                key: "focus",
                label: t("sesion.contexto.focoClinico"),
                children:
                  Array.isArray(sceneFocus) && sceneFocus.length ? (
                    <div className="revi-session-context__tags">
                      {sceneFocus.map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary">—</Text>
                  ),
              },
              {
                key: "guide",
                label: t("sesion.contexto.guiaSala"),
                children: sceneInterventionGuide ? (
                  <HtmlOrText value={sceneInterventionGuide} />
                ) : (
                  <Text type="secondary">{t("sesion.contexto.sinGuion")}</Text>
                ),
              },
              {
                key: "warnings",
                label: t("sesion.contexto.precauciones"),
                children: sceneContraindications ? (
                  <Alert
                    type="warning"
                    showIcon
                    title={t("sesion.contexto.precauciones")}
                    description={
                      <TextoClinico valor={sceneContraindications} />
                    }
                  />
                ) : (
                  <Text type="secondary">
                    {t("sesion.contexto.sinPrecauciones")}
                  </Text>
                ),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="revi-session-context">
      <Tabs defaultActiveKey="scene" size="small" items={items} />
    </div>
  );
}
